import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GeoId } from "../../types/geo.types";
import { Button, ButtonTheme, ButtonVariant, MapLayout } from "../../ui-elements";
import { HexLoadingToast } from "../Shadcn";
import { useMap } from "@vis.gl/react-google-maps";
import { LuArrowUpRight } from "react-icons/lu";
import { createSearchParams, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { DeckGLOverlay, ResetZoomButton, MapLayers, POIHoverPopup, MBPHoverPopup, GeoHoverCard, AggUnitControl } from "../home";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { brandList, checkboxState, cifTypeState, choroplethModeState, collisionRadiusKmState, dynamicPoiTogglesState, filterLoadingState, filtersState, focusedPoiState, geoJsonState, geoState, hexLoadingState, hoveredBrandState, loggedUserState, markerCarouselState, metricState, recommendedRegionsState, selectedBrands, selectedCategoricalLayerState, selectedTabState, selAggLayerState, selPOICategories, showDrawerState, userCurrencyState, dynamicTogglesState } from "../../states";
import { checkIsBrandSelected, checkOutOfBoundsUsingPosition, cleanFilterTree, getCollisionRadiusKm, getCollidingMarkers, getFirstCoordinate, latLngToPixel, normalizeLatLng, removeParentFeature, useHandleError, useMapBounds, useMapZoomControls } from "../../helpers";
import { useStreetView } from "../../helpers/streetviewHook";
import { useMapsService } from "../../services";
import equal from "fast-deep-equal";
import { RESET_FIELD_ARRAY, RouteConstants } from "../../constants";
import { useQuery } from "@tanstack/react-query";

type HomeOutletContext = {
    toggleSwitch: (e: any, name?: string, value?: boolean) => void;
    myPresence: { info: any[], markers: any[] }
    mapColorShimmer: boolean;
    setMapColorShimmer: React.Dispatch<React.SetStateAction<boolean>>;
    mbpFilter: any;
    setMbpFilter: React.Dispatch<React.SetStateAction<any>>;
    setIsPoiOverlapped: React.Dispatch<React.SetStateAction<boolean>>;
    selCatchmentType: any;
    catchment: number;
};

interface CatchmentMapProps {
    catchmentId?: GeoId;
    myPresenceMarkers?: any[];
    tableClickedPoi?: any;
    setTableClickedPoi?: React.Dispatch<React.SetStateAction<any>>;
    tableClickedMbp?: any;
    setTableClickedMbp?: React.Dispatch<React.SetStateAction<any>>;
    carouselHandlersRef?: MutableRefObject<{
        onMarkerSelect?: (marker: { type: string; data: any }) => void;
        onClose?: () => void;
    }>;
}

export const CatchmentMap = ({ catchmentId, myPresenceMarkers, tableClickedPoi, setTableClickedPoi, tableClickedMbp, setTableClickedMbp, carouselHandlersRef }: CatchmentMapProps) => {
    const MAP_TYPE_ID = process.env.REACT_APP_MAP_TYPE_ID;
    const SILVER_MAP_ID = process.env.REACT_APP_MAP_ID_1;

    // Get props from HomeLayout outlet context if not provided
    const outletContext = useOutletContext<HomeOutletContext>();

    const toggleSwitch = outletContext?.toggleSwitch;
    const myPresence = myPresenceMarkers ? { info: [], markers: myPresenceMarkers } : outletContext?.myPresence || { info: [], markers: [] };
    const mapColorShimmer = outletContext?.mapColorShimmer;
    const setMapColorShimmer = outletContext?.setMapColorShimmer;
    const mbpFilter = outletContext?.mbpFilter;
    const setMbpFilter = outletContext?.setMbpFilter;
    const setIsPoiOverlapped = outletContext?.setIsPoiOverlapped;
    const selCatchmentType = outletContext?.selCatchmentType;
    const catchment = catchmentId || outletContext?.catchment || 0;
    const map = useMap();
    const mapService = useMapsService();
    const isGeoFocusedRef = useRef<boolean>(false);
    const loggedUser = useRecoilValue(loggedUserState);
    const cifType = useRecoilValue(cifTypeState);
    const isChecked = useRecoilValue(checkboxState);
    const geoJSON = useRecoilValue(geoJsonState);
    const setGeoJSON = useSetRecoilState(geoJsonState);
    const selectedTab = useRecoilValue(selectedTabState);
    const selCurrency = useRecoilValue(userCurrencyState);
    const selCategoryList = useRecoilValue(selPOICategories);
    const selMetric = useRecoilValue(metricState);
    const isFilterLoading = useRecoilValue(filterLoadingState);
    const filters = useRecoilValue<any[]>(filtersState);
    const [brands, setBrands] = useRecoilState(brandList);
    const [selBrands, setSelBrands] = useRecoilState(selectedBrands);
    const dynamicPoiToggles = useRecoilValue(dynamicPoiTogglesState);
    const [searchParams] = useSearchParams();
    const [center, setCenter] = useState({ lat: 20.5937, lng: 78.9629 });
    const [mapType, setMapType] = useState<'roadmap' | 'hybrid' | 'map' | 'dark'>(localStorage.getItem("map-style") as 'roadmap' | 'hybrid' | 'map' | 'dark' || 'roadmap');
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [isStreetViewEnabled, setIsStreetViewEnabled] = useState<boolean>(false);
    const [focusedPoi, setFocusedPoi] = useRecoilState(focusedPoiState);
    const [hoveredBrand, setHoveredBrand] = useRecoilState<any>(hoveredBrandState);
    const [hoveredPresence, setHoveredPresence] = useState<any | null>(null);
    const [open, setOpen] = useRecoilState(showDrawerState);
    const setRecommendedRegionsPayload = useSetRecoilState(recommendedRegionsState);
    const dynamicToggles = useRecoilValue(dynamicTogglesState);
    const choroplethMode = useRecoilValue(choroplethModeState);
    const selectedCategoricalLayer = useRecoilValue(selectedCategoricalLayerState);
    const [zoom, setZoom] = useState({ isZoomed: false, zoomLevel: 0, center: map?.getCenter() ?? { lat: 20.5937, lng: 78.9629 } });
    const [mapProps, setMapProps] = useState<any>();
    const [defaultMapInfo, setDefaultMapInfo] = useState<{ bounds: any, zoom: number }>({ bounds: null, zoom: 1 });
    const [hoverData, setHoverData] = useState<any>();
    const isHoveredOnMarker = useRef<{ poi: boolean, mbp: boolean }>({ poi: false, mbp: false })
    const outOfBoundSideRef = useRef<string[]>([]);
    const mapPropsPayloadRef = useRef<any>(null);
    const [selectedGeo, setselectedGeo] = useRecoilState(geoState);
    const selAggLayer = useRecoilValue(selAggLayerState);
    const { roiBoundsRef, poiBoundRef, mbpBoundRef, updateBounds } = useMapBounds();
    const { getErrorMsg } = useHandleError();
    const { handleZoomOutStyle, handleWheel, handleTouch, handleDragEnd, handleResetBound } = useMapZoomControls();

    // Combined carousel state — shared via Recoil so Catchments.tsx can render CollisionTablePanel
    const [markerCarousel, setMarkerCarousel] = useRecoilState(markerCarouselState);
    const setCollisionRadiusKm = useSetRecoilState(collisionRadiusKmState);
    const prePanCenterRef = useRef<google.maps.LatLng | null>(null);

    // Track zoom so collisionRadiusKm stays current after zoom changes
    const [mapZoom, setMapZoom] = useState<number>(map?.getZoom() ?? 1);
    useEffect(() => {
        if (!map) return;
        const listener = map.addListener('zoom_changed', () => setMapZoom(map.getZoom() ?? 1));
        return () => listener.remove();
    }, [map]);

    // State for tracking hex and non-hex data loading
    const [hexLoading, setHexLoading] = useRecoilState(hexLoadingState);
    const [showHexToast, setShowHexToast] = useState(false);

    const [boundaryLoadingState, setBoundaryLoadingState] = useState<{
        loading: boolean;
        firstChunkReceived: boolean;
    }>({ loading: false, firstChunkReceived: false });

    // Refs to accumulate features from both hex and non-hex queries
    const accumulatedFeaturesRef = useRef<{
        rootProperties: any;
        hexFeatures: any[];
        nonHexFeatures: any[];
    }>({ rootProperties: null, hexFeatures: [], nonHexFeatures: [] });

    // Ref to track current aggregation layer for use in callbacks
    const selAggLayerRef = useRef(selAggLayer);
    useEffect(() => {
        selAggLayerRef.current = selAggLayer;
    }, [selAggLayer]);

    // Carousel state for GeoJSON features
    const [geoCarousel, setGeoCarousel] = useState<{
        features: any[];
        currentIndex: number;
    } | null>(null);

    useStreetView(map, setIsStreetViewEnabled);

    // Set up click functionality for zoom buttons
    useEffect(() => {
        const zoomOutButton: any = document.querySelector('button[title="Zoom out"]');
        const zoomInButton: any = document.querySelector('button[title="Zoom in"]');
        const pegmanButton: any = document.querySelector('button[title="Drag Pegman onto the map to open Street View"]');

        const handleZoomClick = () => {
            handleCloseRegionCard();
        };

        const handlePegmanMouseDown = () => {
            handleCloseRegionCard();
        };

        if (zoomOutButton && zoomInButton) {
            zoomOutButton.addEventListener('click', handleZoomClick, true);
            zoomInButton.addEventListener('click', handleZoomClick, true);
            pegmanButton?.addEventListener('mousedown', handlePegmanMouseDown, true);
        }

        // Cleanup function to remove event listener when component unmounts
        return () => {
            if (zoomOutButton && zoomInButton) {
                zoomOutButton.removeEventListener('click', handleZoomClick, true);
                zoomInButton.removeEventListener('click', handleZoomClick, true);
                pegmanButton?.removeEventListener('mousedown', handlePegmanMouseDown, true);
            }
        };
    }, [map?.getBounds()]);

    const highlightedPoIs = useMemo(() => {
        if (brands?.poimarker?.length === 0) return [];

        return brands?.poimarker?.flatMap((brand) =>
            brand.branches.map((branch: any) => ({
                ...branch,
                isSelected: checkIsBrandSelected(branch.branchId, selBrands),
            }))
        );
    }, [brands, selBrands]);

    const otherMarkers = useMemo(() => {
        const result: Record<string, any[]> = {};
        Object.entries(brands || {}).forEach(([key, value]) => {
            if (key === 'poimarker' || !dynamicPoiToggles[key]) return;
            result[key] = value as any[];
        });
        return result;
    }, [brands, dynamicPoiToggles]);

    // Placeholder handlers - replace with actual implementations
    const handleMouseout = () => {
        setHoveredBrand(null);
        setHoveredPresence(null);
        setFocusedPoi({ type: null, data: null });
    };

    const handleMapType = (type: 'roadmap' | 'hybrid' | 'map' | 'dark') => {
        setMapType(type);
        localStorage.setItem("map-style", type);
    }

    const onPanChange = (map: google.maps.Map | null) => {
        if (map) {
            updateBounds(map);
        }
    };

    const handleCloseRegionCard = () => {
        isGeoFocusedRef.current = false;
        setHoverData(null);
        setGeoCarousel(null);
    }

    const handlePopupHover = (type: string, poi?: any, screenCoord?: number[], focusedPoi?: any) => {
        // Always update marker state for click detection
        isHoveredOnMarker.current = poi ? { ...isHoveredOnMarker.current, [type]: true } : { ...isHoveredOnMarker.current, [type]: false };

        // Don't show hover popup when a card is focused or region is focused
        if (focusedPoi?.type || isGeoFocusedRef.current) {
            return;
        }

        if (type === 'mbp') {
            if (!poi) setHoveredPresence(null);
            else if (poi?.unique_key !== hoveredPresence?.unique_key) setHoveredPresence({ ...poi, x: screenCoord?.[0], y: screenCoord?.[1] });
            setHoveredBrand(null);
        } else {
            // 'poi' and all other marker types
            if (!poi) setHoveredBrand(poi);
            else if (poi?.branchId !== hoveredBrand?.branchId) setHoveredBrand({ ...poi, x: screenCoord?.[0], y: screenCoord?.[1], _markerType: type });
            setHoveredPresence(null);
        }

        if (poi || focusedPoi?.type) {
            setHoverData(null);
        }
    };

    // Ray-casting point-in-polygon for GeoJSON geometries (coordinates in [lng, lat] format)
    const pointInPolygon = (lng: number, lat: number, geometry: any): boolean => {
        const isInRing = (ring: [number, number][]): boolean => {
            let inside = false;
            for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
                const [xi, yi] = ring[i];
                const [xj, yj] = ring[j];
                if (((yi > lat) !== (yj > lat)) && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi)) {
                    inside = !inside;
                }
            }
            return inside;
        };
        if (geometry.type === 'Polygon') return isInRing(geometry.coordinates[0]);
        if (geometry.type === 'MultiPolygon') return geometry.coordinates.some((poly: any) => isInRing(poly[0]));
        return false;
    };

    const handlePoiClick = (type: string | null, data: any, screenCoord?: number[]) => {
        // Close any open region hover card before opening presence/poi card
        if (isGeoFocusedRef.current && data?.geoLevel !== 'catchment') {
            handleCloseRegionCard();
        }

        // First clear both hover states to ensure clean slate
        setHoveredPresence(null);
        setHoveredBrand(null);

        // Build combined carousel from colliding MBPs + POIs at the clicked location
        if ((type === 'mbp' || type === 'poi') && data && map) {
            const radius = type === 'mbp' ? 27.17 : 5;

            const collidingMBPs = myPresence?.markers && Array.isArray(myPresence.markers) && myPresence.markers.length > 0
                ? getCollidingMarkers(data, myPresence.markers, map, radius, 'unique_key')
                : [];

            const collidingPOIs = isChecked?.poimarker && highlightedPoIs && Array.isArray(highlightedPoIs) && highlightedPoIs.length > 0
                ? getCollidingMarkers(data, highlightedPoIs, map, radius, 'branchId')
                : [];

            const clickedItem = { type: type as string, data };
            const otherMBPs = collidingMBPs.map((m: any) => ({ type: 'mbp' as const, data: m }));
            const otherPOIs = collidingPOIs.map((p: any) => ({ type: 'poi' as const, data: p }));

            const allMarkers: Array<{ type: string; data: any }> = type === 'mbp'
                ? [clickedItem, ...otherMBPs, ...otherPOIs]
                : [clickedItem, ...otherPOIs, ...otherMBPs];

            if (allMarkers.length > 1) {
                const containingFeature = removeParentFeature(geoJSON, selectedGeo, selectedTab, !!catchment)?.features?.find((f: any) =>
                    pointInPolygon(data.longitude, data.latitude, f.geometry)
                );
                const markersWithRegion = containingFeature
                    ? [...allMarkers, { type: 'region' as const, data: containingFeature.properties }]
                    : allMarkers;
                setMarkerCarousel({ markers: markersWithRegion, currentIndex: 0 });

                const clickY = screenCoord?.[1];
                const markerLat = data.latitude;
                const markerLng = data.longitude;
                setTimeout(() => {
                    if (!map) return;
                    const mapHeight = map.getDiv()?.clientHeight ?? window.innerHeight;
                    const projPixel = latLngToPixel(map, markerLat, markerLng);
                    const markerY = projPixel?.y ?? clickY;
                    if (markerY != null && markerY > mapHeight * 0.60) {
                        prePanCenterRef.current = map.getCenter() ?? null;
                        map.panBy(0, markerY - mapHeight * 0.45);
                        google.maps.event.addListenerOnce(map, 'idle', () => onPanChange(map));
                    }
                }, 0);
            } else {
                setMarkerCarousel(null);
                if (prePanCenterRef.current && map) {
                    map.panTo(prePanCenterRef.current);
                    prePanCenterRef.current = null;
                }
            }
        } else if (type !== 'mbp' && type !== 'poi') {
            setMarkerCarousel(null);
        }

        // Then set the clicked item as hovered so it displays
        if (type === 'mbp') {
            setTimeout(() => setHoveredPresence({ ...data, x: screenCoord?.[0], y: screenCoord?.[1] }), 0);
        } else if (type) {
            // 'poi' and all other marker types
            setTimeout(() => setHoveredBrand({ ...data, x: screenCoord?.[0], y: screenCoord?.[1], _markerType: type }), 0);
        }

        // Always set the new focused POI/presence to show close button and track focused state
        setFocusedPoi({ type, data, x: screenCoord?.[0], y: screenCoord?.[1] });
    }

    // Handle POI click from table view
    useEffect(() => {
        if (tableClickedPoi) {
            handlePoiClick('poi', tableClickedPoi);
            setTableClickedPoi?.(null);
        }
    }, [tableClickedPoi]);

    // Handle MBP click from table view
    useEffect(() => {
        if (tableClickedMbp) {
            // Table rows may not have lat/lng; look up the full marker from myPresence
            const matchedMarker = tableClickedMbp.latitude && tableClickedMbp.longitude
                ? tableClickedMbp
                : myPresence?.markers?.find((m: any) =>
                    String(m.unique_key) === String(tableClickedMbp.unique_key)
                );
            if (matchedMarker) {
                handlePoiClick('mbp', matchedMarker);
            } else {
                // No coordinates available — just highlight the row without showing the card
                setFocusedPoi({ type: 'mbp', data: tableClickedMbp });
            }
            setTableClickedMbp?.(null);
        }
    }, [tableClickedMbp]);

    const onMouseOver = (x: number, y: number, object: any, selectedTab: number) => {
        if (!mapProps || !mapProps.properties) return;

        // Handle mouse leaving the feature
        if (!object || (hoveredPresence || hoveredBrand)) {
            if (!isGeoFocusedRef.current) setHoverData(null);
            return;
        }

        if (!isGeoFocusedRef.current) outOfBoundSideRef.current = checkOutOfBoundsUsingPosition({ x, y }, roiBoundsRef.current);

        // Handle both old structure (geoId) and new structure (hexGeoId)
        const actualGeoId = object?.hexGeoId || object?.geoId;
        updatedHoverData(actualGeoId, selectedTab, { x, y });
    }

    //navigate to the clicked geo
    const onGeoClick = (geoId: GeoId, geoLevel: string, coordinates: { x: number, y: number }, data: any) => {
        if (isHoveredOnMarker?.current?.mbp) return;

        // Close any open presence/poi card before opening region card
        if (focusedPoi?.type) {
            setFocusedPoi({ type: null, data: null });
            setHoveredPresence(null);
            setHoveredBrand(null);
            isHoveredOnMarker.current.mbp = false;
            isHoveredOnMarker.current.poi = false;
        }

        // Temporarily reset focus to allow hover data update
        isGeoFocusedRef.current = false;

        // Update hover data for the new region
        if (coordinates) {
            outOfBoundSideRef.current = checkOutOfBoundsUsingPosition(coordinates, roiBoundsRef.current);
            updatedHoverData(geoId, selectedTab, coordinates);
        }

        // No carousel on direct region click — clear both carousel states
        setMarkerCarousel(null);
        setGeoCarousel(null);

        // Set focus back to true after updating
        isGeoFocusedRef.current = true;
    }


    //display the geo info popup on hover over the geojson item
    const updatedHoverData = (geoId: GeoId, selectedTab: number, coordinates: { x: number, y: number }) => {
        // If card is focused (clicked), don't update hover data at all
        if (isGeoFocusedRef.current) return;

        // Handle both old structure (geoId) and new structure (hexGeoId)
        const found: any = mapProps?.properties?.find((item: any) => item.geoId === geoId || item.hexGeoId === geoId);

        if (found) {
            setHoverData((prev: any) => {
                if ((prev?.geoId === geoId) || (prev?.hexGeoId === geoId)) {
                    return { ...prev, ...coordinates };
                }
                return { ...found, ...coordinates };
            });
        } else {
            setHoverData(null);
        }
    };

    const handleMapIdle = useCallback(() => {
        updateBounds(map);
    }, [updateBounds]);

    // Geo carousel navigation handlers
    const handleGeoCarouselNext = () => {
        if (!geoCarousel || !map) return;
        const nextIndex = (geoCarousel.currentIndex + 1) % geoCarousel.features.length;
        const nextFeature = geoCarousel.features[nextIndex];
        setGeoCarousel({ ...geoCarousel, currentIndex: nextIndex });

        // Anchor popup to the first coordinate of the feature's geometry
        const geoFeature = geoJSON?.features?.find((f: any) => f.properties.geoId === nextFeature.geoId);
        if (geoFeature) {
            const [lng, lat] = getFirstCoordinate(geoFeature.geometry.coordinates);
            const pixel = latLngToPixel(map, lat, lng);
            if (pixel) {
                outOfBoundSideRef.current = checkOutOfBoundsUsingPosition(pixel, roiBoundsRef.current);
                setHoverData({ ...nextFeature, ...pixel });
                return;
            }
        }
        setHoverData((prev: any) => ({ ...nextFeature, x: prev?.x, y: prev?.y }));
    };

    const handleGeoCarouselPrev = () => {
        if (!geoCarousel || !map) return;
        const prevIndex = geoCarousel.currentIndex === 0
            ? geoCarousel.features.length - 1
            : geoCarousel.currentIndex - 1;
        const prevFeature = geoCarousel.features[prevIndex];
        setGeoCarousel({ ...geoCarousel, currentIndex: prevIndex });

        // Anchor popup to the first coordinate of the feature's geometry
        const geoFeature = geoJSON?.features?.find((f: any) => f.properties.geoId === prevFeature.geoId);
        if (geoFeature) {
            const [lng, lat] = getFirstCoordinate(geoFeature.geometry.coordinates);
            const pixel = latLngToPixel(map, lat, lng);
            if (pixel) {
                outOfBoundSideRef.current = checkOutOfBoundsUsingPosition(pixel, roiBoundsRef.current);
                setHoverData({ ...prevFeature, ...pixel });
                return;
            }
        }
        setHoverData((prev: any) => ({ ...prevFeature, x: prev?.x, y: prev?.y }));
    };

    // Sync collisionRadiusKm to Recoil whenever carousel or zoom changes
    useEffect(() => {
        if (!markerCarousel?.markers.length) { setCollisionRadiusKm(null); return; }
        const ref = markerCarousel.markers.find(m => m.type !== 'region');
        if (!ref) { setCollisionRadiusKm(null); return; }
        const pixelRadius = ref.type === 'mbp' ? 27.17 : 5;
        setCollisionRadiusKm(getCollisionRadiusKm(map, ref.data.latitude, pixelRadius));
    }, [markerCarousel, mapZoom, map]);

    // Shared logic: update carousel to a specific index and sync all popup states
    const goToCarouselIndex = (newIndex: number) => {
        if (!markerCarousel) return;

        const newItem = markerCarousel.markers[newIndex];
        const anchorX = hoveredPresence?.x ?? hoveredBrand?.x ?? hoverData?.x;
        const anchorY = hoveredPresence?.y ?? hoveredBrand?.y ?? hoverData?.y;

        setMarkerCarousel({ ...markerCarousel, currentIndex: newIndex });

        if (newItem.type === 'region') {
            setHoveredPresence(null);
            setHoveredBrand(null);
            isGeoFocusedRef.current = true;
            const geoId = newItem.data.geoId ?? newItem.data.hexGeoId;
            // Look up enriched API data (EH, CTV, PoI, Population) — same source as updatedHoverData
            const enrichedData = mapProps?.properties?.find(
                (item: any) => item.geoId === geoId || item.hexGeoId === geoId
            ) ?? newItem.data;
            if (enrichedData?.geoLevel === 'catchment') {
                setFocusedPoi({ type: 'mbp', data: enrichedData });
            } else {
                setFocusedPoi({ type: null, data: null });
            }
            if (map) {
                const regionFeature = geoJSON?.features?.find((f: any) => {
                    const fId = f.properties.hexGeoId || f.properties.geoId;
                    return fId === geoId;
                });
                if (regionFeature) {
                    const [lng, lat] = getFirstCoordinate(regionFeature.geometry.coordinates);
                    const pixel = latLngToPixel(map, lat, lng);
                    if (pixel) {
                        outOfBoundSideRef.current = checkOutOfBoundsUsingPosition(pixel, roiBoundsRef.current);
                        setHoverData({ ...enrichedData, ...pixel });
                        return;
                    }
                }
            }
            setHoverData({ ...enrichedData, x: anchorX, y: anchorY });
        } else if (newItem.type === 'mbp') {
            setHoveredPresence({ ...newItem.data, x: anchorX, y: anchorY });
            setHoveredBrand(null);
            setFocusedPoi({ type: 'mbp', data: newItem.data });
            isGeoFocusedRef.current = false;
            setHoverData(null);
        } else {
            setHoveredBrand({ ...newItem.data, x: anchorX, y: anchorY, _markerType: newItem.type });
            setHoveredPresence(null);
            setFocusedPoi({ type: newItem.type, data: newItem.data });
            isGeoFocusedRef.current = false;
            setHoverData(null);
        }
    };

    // Combined carousel navigation
    const navigateCarousel = (direction: 'next' | 'prev') => {
        if (!markerCarousel) return;
        const total = markerCarousel.markers.length;
        const newIndex = direction === 'next'
            ? (markerCarousel.currentIndex + 1) % total
            : markerCarousel.currentIndex === 0 ? total - 1 : markerCarousel.currentIndex - 1;
        goToCarouselIndex(newIndex);
    };

    const handleCarouselNext = () => navigateCarousel('next');
    const handleCarouselPrev = () => navigateCarousel('prev');

    // Table row click — find the clicked marker in the carousel and navigate to it
    const handleTableRowSelect = (marker: { type: string; data: any }) => {
        if (!markerCarousel) return;
        const index = markerCarousel.markers.findIndex(m => {
            if (m.type !== marker.type) return false;
            if (m.type === 'poi')    return m.data.branchId   === marker.data.branchId;
            if (m.type === 'mbp')    return m.data.unique_key === marker.data.unique_key;
            if (m.type === 'region') return m.data.geoId      === marker.data.geoId;
            return false;
        });
        if (index !== -1) goToCarouselIndex(index);
    };

    // Expose carousel handlers to the parent (Catchments.tsx)
    useEffect(() => {
        if (!carouselHandlersRef) return;
        carouselHandlersRef.current.onMarkerSelect = handleTableRowSelect;
        carouselHandlersRef.current.onClose = () => {
            setMarkerCarousel(null);
            setFocusedPoi({ type: null, data: null });
            if (prePanCenterRef.current && map) {
                map.panTo(prePanCenterRef.current);
                prePanCenterRef.current = null;
            }
        };
    });

    const handlePoiClose = (e: any) => {
        e.stopPropagation();
        if (focusedPoi) {
            if (focusedPoi?.data?.geoLevel === 'catchment') {
                setHoverData(null);
                isGeoFocusedRef.current = false;
            }
            setFocusedPoi({ type: null, data: null });
            setHoveredPresence(null);
            setHoveredBrand(null);
            isHoveredOnMarker.current.mbp = false;
            isHoveredOnMarker.current.poi = false;
            setMarkerCarousel(null);
            if (prePanCenterRef.current && map) {
                map.panTo(prePanCenterRef.current);
                prePanCenterRef.current = null;
            }
        }
    }

    const handleNavigation = (e: any, resetFields: any, navigation: any, geoLevel: string) => {
        e.stopPropagation();
        handleCloseRegionCard();
        setFocusedPoi({ type: null, data: null });
        setHoveredBrand(null);
        setHoveredPresence(null);

        if (geoLevel === 'catchment') {
            searchParams.set('catchment', navigation?.catchment);
            navigate({
                pathname: RouteConstants.catchments,
                search: `?${searchParams.toString()}`,
            });
            return;
        }

        const selGeo: Record<string, any> = {};

        resetFields.forEach((key: string) => {
            if (key === 'pincode' || key === 'sub_district')
                key = selectedTab === 4 ? 'sub_district' : 'pincode';
            let value = navigation[key];

            if (value !== undefined && value !== null && value !== "") {
                if (key === 'pincode') selGeo['sub_district'] = value;
                else selGeo[key] = value;
            }
        });

        setselectedGeo(selGeo);
        // Navigate to dashboard with updated params
        setTimeout(() => {
            const newSearchParams = createSearchParams(selGeo);
            navigate(RouteConstants.root + "?" + newSearchParams);
        }, 0);
    }

    const handleNavigationFromRegionCard = (e: any, navigation: any) => {
        e.stopPropagation();
        handleCloseRegionCard();
        setFocusedPoi({ type: null, data: null });
        setHoveredBrand(null);
        setHoveredPresence(null);

        const selGeo: Record<string, any> = {};

        RESET_FIELD_ARRAY['all'].forEach((key: string) => {
            let value = navigation[key];

            if (value !== undefined && value !== null && value !== "") {
                if (key === 'pincode') selGeo['sub_district'] = value;
                else selGeo[key] = value;
            }
        });
        setselectedGeo(selGeo);

        // Navigate to dashboard with updated params
        setTimeout(() => {
            const newSearchParams = createSearchParams(selGeo);
            navigate(RouteConstants.root + "?" + newSearchParams);
        }, 0);
    }

    //fetch map props
    const fetchMapProps = (payload: any) => {
        // setMapSpinner(prev => prev + 1);
        mapService.getMapProperties(payload).then((response: any) => {
            setMapProps(response);
        }).catch((error: any) => {
            getErrorMsg(error);
        })
        // .finally(() => setMapSpinner(prev => prev - 1));
    }

    // Helper function to merge features and update geoJSON state
    const mergeAndUpdateGeoJSON = () => {
        const { rootProperties, hexFeatures, nonHexFeatures } = accumulatedFeaturesRef.current;

        // Filter features based on selected aggregation layer (use ref to get latest value)
        let features: any[] = [];

        if (selAggLayerRef.current?.key === 'hexgrid') {
            // Show hex features only when hex grid is selected
            features = [...nonHexFeatures, ...hexFeatures];
        } else {
            // Show only non-hex features (sub-region boundaries)
            features = [...nonHexFeatures];
        }

        const newGeoJSON = {
            type: 'FeatureCollection',
            rootProperties,
            features
        };

        setGeoJSON(newGeoJSON);
    };

    // Determine if we should fetch hex data (always fetch for catchments)
    const shouldFetchHex = !!catchment;

    // Query for non-hex boundaries (lighter data, loads quickly)
    const { isLoading: isBoundaryLoading } = useQuery({
        queryKey: ['catchmentMapBoundaries', catchment],
        queryFn: async ({ signal }) => {
            if (!catchment) return null;

            // Reset accumulated features for non-hex
            accumulatedFeaturesRef.current.nonHexFeatures = [];
            setBoundaryLoadingState({ loading: true, firstChunkReceived: false });

            setShowHexToast(true);
            setHexLoading({ loading: true, firstChunkReceived: false, progress: 0 });

            return mapService.getMap(
                (catchment),
                false, // hex=false
                undefined,
                undefined,
                signal,
                (progressData) => {
                    // Handle progress updates
                    if (progressData.type === 'rootProperties') {
                        accumulatedFeaturesRef.current.rootProperties = progressData.data;
                        mergeAndUpdateGeoJSON();
                    } else if (progressData.features) {
                        accumulatedFeaturesRef.current.nonHexFeatures = [
                            ...accumulatedFeaturesRef.current.nonHexFeatures,
                            ...progressData.features
                        ];

                        // Mark first chunk as received
                        if (!boundaryLoadingState.firstChunkReceived) {
                            setBoundaryLoadingState({ loading: true, firstChunkReceived: true });
                        }

                        mergeAndUpdateGeoJSON();
                    } else if (progressData.status === 'complete') {
                        setBoundaryLoadingState({ loading: false, firstChunkReceived: true });
                    }
                }
            );
        },
        enabled: !!selCurrency && !!catchment,
        staleTime: 0,
        gcTime: 0,
    });

    // Query for hex data (heavy data, loads progressively)
    const { isLoading: isHexLoading } = useQuery({
        queryKey: ['catchmentMapHexData', catchment],
        queryFn: async ({ signal }) => {
            if (!catchment || !shouldFetchHex) return null;

            // Reset accumulated features for hex
            accumulatedFeaturesRef.current.hexFeatures = [];

            return mapService.getMap(
                (catchment),
                true, // hex=true
                undefined,
                undefined,
                signal,
                (progressData) => {
                    // Handle progress updates
                    if (progressData.status === 'started') {
                        setShowHexToast(true);
                        setHexLoading(prev => ({ ...prev, loading: true }));
                    } else if (progressData.features) {
                        accumulatedFeaturesRef.current.hexFeatures = [
                            ...accumulatedFeaturesRef.current.hexFeatures,
                            ...progressData.features
                        ];

                        // Mark first chunk as received (switch from loader to progress indicator)
                        if (!hexLoading.firstChunkReceived) {
                            setHexLoading(prev => ({ ...prev, firstChunkReceived: true }));
                        }

                        // Update progress (rough estimate based on feature count)
                        const estimatedProgress = Math.min(95, (progressData.totalFeatures / 1000) * 100);
                        setHexLoading(prev => ({ ...prev, progress: estimatedProgress }));

                        mergeAndUpdateGeoJSON();
                    } else if (progressData.status === 'complete') {
                        setHexLoading({ loading: false, firstChunkReceived: true, progress: 100 });
                        setTimeout(() => setShowHexToast(false), 2000);
                    }
                }
            );
        },
        enabled: !!selCurrency && !!catchment && shouldFetchHex,
        staleTime: 0,
        gcTime: 0,
    });

    // Reset accumulated features when catchment changes
    useEffect(() => {
        accumulatedFeaturesRef.current = {
            rootProperties: null,
            hexFeatures: [],
            nonHexFeatures: []
        };
        setHexLoading({ loading: false, firstChunkReceived: false, progress: 0 });
        setBoundaryLoadingState({ loading: false, firstChunkReceived: false });
        setShowHexToast(false);
    }, [catchment]);

    // Update displayed features when aggregation layer changes (without refetching)
    // This allows instant switching between hex and non-hex views using already-loaded data
    useEffect(() => {
        // Only update if we have data already loaded
        if (accumulatedFeaturesRef.current.rootProperties) {
            mergeAndUpdateGeoJSON();
        }
    }, [selAggLayer?.key]);

    // Handle side effects when catchment changes
    useEffect(() => {
        setZoom({ isZoomed: false, zoomLevel: map?.getZoom() || 0, center: normalizeLatLng(map?.getCenter()) });

        // Clear MBP/POI states when navigating between geo levels
        setHoveredPresence(null);
        setHoveredBrand(null);
        setFocusedPoi({ type: null, data: null });

        if (map) {
            map.setOptions({
                gestureHandling: 'auto',
                draggable: true
            });
        }
        handleCloseRegionCard();
    }, [catchment, selCurrency]);

    useEffect(() => {
        if (!isChecked?.poimarker) {
            handleCloseRegionCard();
            setHoveredPresence(null);
            setHoveredBrand(null);
            setFocusedPoi({ type: null, data: null });
            setMarkerCarousel(null);
        }
    }, [isChecked?.poimarker]);

    // Display geoJSON data in the map
    useEffect(() => {
        if (Object.keys(geoJSON).length && map) {
            let boundsListener: google.maps.MapsEventListener | undefined;
            let zoomListener: google.maps.MapsEventListener | undefined;
            let dragListener: google.maps.MapsEventListener | undefined;
            let onPanChangeListener: google.maps.MapsEventListener | undefined;
            let wheelHandler: ((event: WheelEvent) => void) | undefined;
            let touchHandler: ((event: TouchEvent) => void) | undefined;

            const processCoordinates = (coordinates: any): void => {
                if (Array.isArray(coordinates[0])) {
                    // Multi-part geometry, like a polygon with holes or a multi-line string
                    coordinates.forEach((coordSet: any) => {
                        processCoordinates(coordSet);
                    });
                } else {
                    // Single set of coordinates
                    bounds.extend(new window.google.maps.LatLng(coordinates[1], coordinates[0]));
                }
            };

            const bounds = new window.google.maps.LatLngBounds();
            geoJSON?.features.forEach((feature: any) => {
                processCoordinates(feature.geometry.coordinates);
            });

            if (map) {
                map.setOptions({
                    gestureHandling: 'auto',
                    draggable: true
                });
            }
            // Set map center and zoom level based on bounding box
            map?.fitBounds(bounds, 20);

            setTimeout(() => {
                if (zoom.isZoomed) {
                    map?.setZoom(zoom.zoomLevel);
                    map?.setCenter(zoom.center);
                    if (zoom.zoomLevel <= defaultMapInfo.zoom - 1) {
                        map.setOptions({
                            gestureHandling: 'greedy',
                            minZoom: defaultMapInfo.zoom - 1,
                            draggable: false
                        });
                    }
                }
            }, 0);

            boundsListener = window.google.maps.event.addListenerOnce(map, "bounds_changed", () => {
                updateBounds(map);
                const initialZoom = !zoom.isZoomed ? (map.getZoom()! || 0) : defaultMapInfo.zoom;

                if (zoomListener) zoomListener.remove();

                zoomListener = map?.addListener("zoom_changed", () => handleZoomOutStyle(initialZoom, map));
                onPanChangeListener = map?.addListener("idle", () => onPanChange(map));
                dragListener = map?.addListener('dragend', () => handleDragEnd(map));

                if (!zoom.isZoomed) setDefaultMapInfo({ bounds, zoom: initialZoom });

                wheelHandler = (event: WheelEvent) => handleWheel(event, map);
                touchHandler = (event: TouchEvent) => handleTouch(event, map, initialZoom);

                map.getDiv().addEventListener("wheel", wheelHandler, { passive: false });
                map.getDiv().addEventListener("touchstart", touchHandler, { passive: false });
            });

            setCenter({ lat: bounds?.getCenter()?.lat(), lng: bounds?.getCenter()?.lng() });

            // Cleanup function
            return () => {
                if (wheelHandler) map.getDiv().removeEventListener("wheel", wheelHandler);
                if (touchHandler) map.getDiv().removeEventListener("touchstart", touchHandler);

                if (zoomListener) zoomListener?.remove();
                if (onPanChangeListener) onPanChangeListener?.remove();
                if (dragListener) dragListener?.remove();
                if (boundsListener) boundsListener.remove();
            };
        }
    }, [map, geoJSON, mapProps, selectedTab, catchment, isChecked?.subregionBoundaries]);

    // Debounce the map color query key to batch rapid successive state changes
    const [debouncedMapColorKey, setDebouncedMapColorKey] = useState({
        catchment, selCategoryList, selCurrency, selMetric, aggLayerKey: selAggLayer?.key, filters, dynamicToggles
    });
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedMapColorKey({
            catchment, selCategoryList, selCurrency, selMetric, aggLayerKey: selAggLayer?.key, filters, dynamicToggles
        }), 50);
        return () => clearTimeout(timer);
    }, [catchment, selCategoryList, selCurrency, selMetric, selAggLayer?.key, filters]);

    // Fetch poi map color using React Query - handles race conditions automatically
    const { isFetching: isMapColorFetching } = useQuery({
        queryKey: ['catchmentMapColor', debouncedMapColorKey],
        queryFn: async ({ signal }) => {
            const { subcategory } = selCategoryList;

            if (!catchment || subcategory.length === 0 || !selCurrency || isFilterLoading) return null;

            const selMetrics = JSON.parse(localStorage?.getItem("selMetrics") || '{}');

            const metric_type = (!selMetrics || Object.values(selMetrics).filter((item: any) => !!item).length === 0)
                ? cifType.map(item => ({ key: item, ismetric: false }))
                : Object.values(selMetrics)
                    .map((metric: any) => {
                        const { title, ...rest } = metric || {};
                        return { ...rest };
                    }) || [];

            const choroplethMetrics = Object.entries(dynamicToggles).map(([id, isOn]) => ({ key: id, ismetric: false, isRank: isOn }));
            const metric_type_final = [...metric_type, ...choroplethMetrics];

            const filters_new = filters.map(cleanFilterTree);

            const payload = {
                subcategory,
                metric_type: metric_type_final,
                filters: filters_new,
                currency: selCurrency,
                geoId: catchment,
                adjacency: false,
                hex: selAggLayer?.key === 'hexgrid',
                ...(choroplethMode === 'categorical' && selectedCategoricalLayer
                    ? { 'categorical_filter': selectedCategoricalLayer }
                    : {}),
            };

            setRecommendedRegionsPayload((prev: any) => ({
                ...prev,
                ...selCategoryList,
                geoId: catchment,
                type: cifType,
                currency: selCurrency,
                metric_type: metric_type_final,
                filters: filters_new,
                adjacency: false,
                catchment: [],
            }));

            const res = await mapService.getMapPoiColor(payload, signal);
            return res;
        },
        enabled: !isFilterLoading,
        staleTime: 50,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (setMapColorShimmer) setMapColorShimmer(isMapColorFetching);
    }, [isMapColorFetching]);

    useEffect(() => {
        const { category, subcategory } = selCategoryList;

        if ((category.length > 0 || subcategory.length > 0) && mbpFilter && catchment && selCurrency) {
            const currentPayload = {
                geoId: catchment,
                type: cifType,
                currency: selCurrency,
                ...selCategoryList,
                mbp_filter: JSON.parse(localStorage?.getItem("mbp-filter")!)?.data,
                adjacency: false,
            };

            const isPayloadEqual = equal(mapPropsPayloadRef.current, currentPayload);

            if (!isPayloadEqual) {
                mapPropsPayloadRef.current = currentPayload;
                fetchMapProps(currentPayload);
            }
        }
    }, [selCategoryList, catchment, selCurrency, mbpFilter]);

    useEffect(() => {
        if (pathname !== RouteConstants.root && catchment && cifType && (selCategoryList.category.length > 0 || selCategoryList.subcategory.length > 0)) {
            // setSpinner(prev => ({ ...prev, spinners: [...prev.spinners, 'getBrands'] }))
            // setShimmerLite((prev: string[]) => [...prev, "getBrands"]);
            const filters_new = filters.map(cleanFilterTree);

            let payload: any = {
                geoId: catchment,
                type: cifType,
                currency: selCurrency,
                filters: filters_new,
                ...selCategoryList,
            };

            mapService.getBrands(payload)
                .then((res: Record<string, any[]>) => {
                    setBrands(res);
                    setSelBrands([]);
                }).
                catch((error: any) => console.log(error))
        }
    }, [catchment, selCategoryList, selCurrency, filters]);

    return (
        <>
            {/* DEBUG: pan trigger zone */}
            {/* <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                height: '43%', zIndex: 9999, pointerEvents: 'none',
                background: 'rgba(255, 0, 0, 0.15)',
                border: '2px dashed red',
                boxSizing: 'border-box',
            }} /> */}

            <div
                style={{ height: '100%', width: '100%', position: 'relative' }}
                className={mapType === 'dark' ? 'dark-zoom-controls' : ''}
            >
                <MapLayout
                    colorScheme={mapType == "dark" ? "DARK" : "LIGHT"}
                    mapTypeId={mapType === 'hybrid' ? 'hybrid' : 'roadmap'}
                    mapId={mapType === 'map' ? MAP_TYPE_ID : SILVER_MAP_ID}
                    gestureHandling={'auto'}
                    zoomControl={pathname != RouteConstants.dashboard}
                    streetViewControl={pathname != RouteConstants.dashboard}
                    defaultCenter={center}
                    // onMouseout={handleMouseout}
                    onIdle={handleMapIdle}
                >
                    {/***** Interactive Overlays Slot *****/}
                    <MapLayout.Overlays>
                        {map &&
                            <DeckGLOverlay
                                key={catchment}
                                geoCode={catchment || 0}
                                showPoi={isChecked?.poimarker}
                                showMbp={!!isChecked?.MBP && Array.isArray(myPresence?.markers) && myPresence?.markers.length > 0}
                                showStories={false}
                                poi={highlightedPoIs}
                                mbp={selectedTab === 4 ? (myPresence?.markers ?? []).filter((poi: any) => !poi.overlap) : myPresence?.markers || []}
                                focusedItem={focusedPoi}
                                onHoverPoi={(type: string, poi: any, pixel?: number[]) => handlePopupHover(type, poi, pixel, focusedPoi)}
                                handleClick={(type: string | null, data: any, pixel?: number[]) => handlePoiClick(type, data, pixel)}
                                brand={hoveredBrand}
                                presence={hoveredPresence}
                                geoJSON={geoJSON}
                                onMapHover={onMouseOver}
                                onMapClick={onGeoClick}
                                isGeoFocused={isGeoFocusedRef.current}
                                mapProps={mapProps}
                                otherMarkers={otherMarkers}
                            />
                        }
                    </MapLayout.Overlays>

                    {/***** Map Controls Slot *****/}
                    <MapLayout.Controls>
                        {!isStreetViewEnabled && pathname === RouteConstants.catchments &&
                            <ResetZoomButton
                                onClick={() => handleResetBound(map, defaultMapInfo.bounds, 20)}
                                onCloseRegionCard={handleCloseRegionCard}
                            />
                        }

                        {!isStreetViewEnabled && !(pathname === RouteConstants.dashboard) &&
                            <MapLayers
                                handleMapType={handleMapType}
                                handleCloseRegionCard={handleCloseRegionCard}
                                mapType={mapType}
                            />}

                        {!isStreetViewEnabled && <AggUnitControl />}
                    </MapLayout.Controls>

                    {/***** Popups *****/}
                    <MapLayout.Popups>
                        {/* Geo Hover Card */}
                        {map && (
                            <GeoHoverCard
                                hoverData={hoverData}
                                outOfBounds={outOfBoundSideRef.current}
                                isGeoFocused={isGeoFocusedRef.current}
                                companyName={loggedUser.company_name}
                                focusedPoi={focusedPoi}
                                mbpRoi={mbpBoundRef.current}
                                onCloseRegion={handleCloseRegionCard}
                                onCloseCatchment={handlePoiClose}
                                onNavigate={handleNavigation}
                                onNavigateFromRegion={handleNavigationFromRegionCard}
                                isCatchmentOn={isChecked?.catchments}
                                carousel={geoCarousel}
                                onCarouselNext={handleGeoCarouselNext}
                                onCarouselPrev={handleGeoCarouselPrev}
                            />
                        )}

                        {/* MBP Hover Popup */}
                        {pathname === RouteConstants.catchments && (
                            <MBPHoverPopup
                                presence={hoveredPresence}
                                companyName={loggedUser.company_name}
                                focusedPoi={focusedPoi}
                                roi={mbpBoundRef.current}
                                onClose={handlePoiClose}
                                onNavigate={handleNavigation}
                                carousel={markerCarousel && markerCarousel.markers[markerCarousel.currentIndex]?.type === 'mbp' ? {
                                    type: 'mbp' as const,
                                    markers: markerCarousel.markers.map(m => m.data),
                                    currentIndex: markerCarousel.currentIndex,
                                    uniqueKeyField: 'unique_key',
                                } : null}
                                onCarouselNext={handleCarouselNext}
                                onCarouselPrev={handleCarouselPrev}
                            />
                        )}

                        {/* POI Hover Popup */}
                        {pathname === RouteConstants.catchments && (
                            <POIHoverPopup
                                brand={hoveredBrand}
                                selectedBrands={selBrands}
                                focusedPoi={focusedPoi}
                                roi={poiBoundRef.current}
                                onClose={handlePoiClose}
                                onNavigate={handleNavigation}
                                carousel={markerCarousel && markerCarousel.markers[markerCarousel.currentIndex]?.type === 'poi' ? {
                                    type: 'poi' as const,
                                    markers: markerCarousel.markers.map(m => m.data),
                                    currentIndex: markerCarousel.currentIndex,
                                    uniqueKeyField: 'branchId',
                                } : null}
                                onCarouselNext={handleCarouselNext}
                                onCarouselPrev={handleCarouselPrev}
                            />
                        )}
                    </MapLayout.Popups>
                </MapLayout>
            </div>

            {/***** Show Table Button - Outside Map *****/}
            {pathname != RouteConstants.dashboard && <Button
                theme={ButtonTheme.secondary}
                variant={ButtonVariant.bordered}
                type='button'
                classname='show-table-view-btn'
                onClick={() => setOpen(!open)}
            >
                <span className="d-flex flex-row align-items-center">
                    Show Table View {<LuArrowUpRight />}
                </span>
            </Button>}

            {showHexToast && pathname === RouteConstants.catchments && (
                <HexLoadingToast
                    progress={hexLoading.progress}
                    isAdjacency={false}
                    onClose={() => setShowHexToast(false)}
                />
            )}
        </>
    );
};